import React, { useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

// Admin: search for a customer, see the full email conversation with them,
// and compose / reply / forward. Reuses the same UnmatchedEmail +
// UnmatchedEmailReply store the unlinked-email queue writes to.
const stripRe = (s) => (s || '').replace(/^\s*(re|fwd|fw)\s*:\s*/i, '').trim();

const quote = (ev) => {
  const who = ev.type === 'reply' ? (ev.author || 'we') : (ev.from_name || ev.from_email || 'the customer');
  const when = new Date(ev.at).toLocaleString();
  const lines = (ev.body || '').split('\n').map((l) => `> ${l}`).join('\n');
  return `\n\n----- On ${when}, ${who} wrote -----\n${lines}\n`;
};

const CustomerEmailsModal = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [convo, setConvo] = useState(null);
  const [convoLoading, setConvoLoading] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [composeMode, setComposeMode] = useState('new'); // 'new' | 'reply' | 'forward'
  const [forwardTo, setForwardTo] = useState('');

  const runSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    try {
      const data = await api.searchCustomers(query.trim());
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Search failed.');
    } finally {
      setSearching(false);
    }
  };

  const resetCompose = () => {
    setComposeMode('new');
    setForwardTo('');
    setSubject('');
    setBody('');
  };

  const openCustomer = async (cust) => {
    setSelected(cust);
    setConvo(null);
    resetCompose();
    setConvoLoading(true);
    try {
      const data = await api.getCustomerEmailConversation(cust.id);
      setConvo(data);
    } catch (err) {
      toast.error('Could not load conversation.');
      setSelected(null);
    } finally {
      setConvoLoading(false);
    }
  };

  const refresh = async () => {
    if (!selected) return;
    try {
      const data = await api.getCustomerEmailConversation(selected.id);
      setConvo(data);
    } catch (err) {
      // keep the current view on a refresh error
    }
  };

  const startReply = (ev) => {
    setComposeMode('reply');
    setForwardTo('');
    setSubject(`Re: ${stripRe(ev.subject)}`);
    setBody(quote(ev));
  };

  const startForward = (ev) => {
    setComposeMode('forward');
    setForwardTo('');
    setSubject(`Fwd: ${stripRe(ev.subject)}`);
    setBody(quote(ev));
  };

  const recipientEmail = convo?.customer?.email || selected?.email;
  const effectiveTo = composeMode === 'forward' ? forwardTo.trim() : recipientEmail;

  const handleSend = async () => {
    if (!body.trim() || !selected) return;
    if (composeMode === 'forward' && !forwardTo.trim()) {
      toast.error('Enter a recipient address to forward to.');
      return;
    }
    setSending(true);
    try {
      await api.sendCustomerEmail(selected.id, {
        subject: subject.trim(),
        body: body.trim(),
        to: composeMode === 'forward' ? forwardTo.trim() : undefined,
      });
      toast.success(composeMode === 'forward' ? 'Email forwarded.' : 'Email sent.');
      resetCompose();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send the email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Customer emails</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {!selected ? (
          <>
            <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>
              Search for a customer to see the full email conversation with them and send a message.
            </p>
            <form onSubmit={runSearch} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <input
                className="form-input" style={{ flex: 1 }} autoFocus
                placeholder="Search by name or email..."
                value={query} onChange={(e) => setQuery(e.target.value)}
              />
              <button type="submit" className="btn-primary" disabled={searching || !query.trim()}>
                {searching ? 'Searching...' : 'Search'}
              </button>
            </form>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {results.length === 0 ? (
                <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>No results yet.</p>
              ) : results.map((cust) => (
                <div
                  key={cust.id}
                  onClick={() => openCustomer(cust)}
                  style={{ cursor: 'pointer', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '0.5rem 0.75rem' }}
                >
                  <strong>{cust.display_name || cust.username}</strong>
                  <span style={{ fontSize: '0.8rem', color: '#aab7c4', marginLeft: '0.5rem' }}>{cust.email || 'no email'}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.5rem' }}>
              <div>
                <strong>{convo?.customer?.name || selected.display_name || selected.username}</strong>
                <span style={{ fontSize: '0.8rem', color: '#aab7c4', marginLeft: '0.5rem' }}>{recipientEmail || 'no email'}</span>
              </div>
              <button type="button" className="btn-secondary" onClick={() => { setSelected(null); setConvo(null); resetCompose(); }}>
                &larr; Back to search
              </button>
            </div>

            {convoLoading ? (
              <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>Loading conversation...</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: 300, overflowY: 'auto', marginBottom: '0.75rem' }}>
                {(!convo || convo.events.length === 0) ? (
                  <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>No emails with this customer yet.</p>
                ) : convo.events.map((ev, i) => (
                  <div key={i} style={{ borderLeft: `3px solid ${ev.type === 'reply' ? 'var(--accent)' : '#5a6b7b'}`, background: 'rgba(255,255,255,0.03)', padding: '0.4rem 0.6rem', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.72rem', color: '#aab7c4', display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span>{ev.type === 'reply' ? `↩ Sent${ev.author ? ` by ${ev.author}` : ''}` : `\u{1F4E5} From ${ev.from_name || ev.from_email || 'customer'}`}</span>
                      <span>{new Date(ev.at).toLocaleString()}</span>
                    </div>
                    {ev.subject && <div style={{ fontSize: '0.78rem', fontWeight: 600, margin: '0.15rem 0' }}>{ev.subject}</div>}
                    <div style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{ev.body || '(empty)'}</div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.3rem' }}>
                      <button
                        type="button" onClick={() => startReply(ev)}
                        style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '0.72rem', padding: 0 }}
                      >
                        Reply
                      </button>
                      <button
                        type="button" onClick={() => startForward(ev)}
                        style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '0.72rem', padding: 0 }}
                      >
                        Forward
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '0.6rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#aab7c4' }}>
                  {composeMode === 'forward' ? 'Forwarding' : composeMode === 'reply' ? `Replying to ${recipientEmail || 'customer'}` : `New message to ${recipientEmail || 'customer'}`}
                </span>
                {composeMode !== 'new' && (
                  <button type="button" className="btn-secondary" style={{ padding: '2px 8px', fontSize: '0.72rem' }} onClick={resetCompose}>
                    New message
                  </button>
                )}
              </div>

              {composeMode === 'forward' && (
                <input
                  className="form-input" style={{ width: '100%', marginBottom: '0.4rem' }}
                  placeholder="Forward to (email address)..."
                  value={forwardTo} onChange={(e) => setForwardTo(e.target.value)}
                />
              )}
              <input
                className="form-input" style={{ width: '100%', marginBottom: '0.4rem' }}
                placeholder="Subject (optional)"
                value={subject} onChange={(e) => setSubject(e.target.value)}
              />
              <textarea
                className="form-input" rows={5} style={{ width: '100%', resize: 'vertical' }}
                placeholder={`Write an email to ${effectiveTo || 'this customer'}...`}
                value={body} onChange={(e) => setBody(e.target.value)}
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                <button
                  type="button" className="btn-primary"
                  disabled={sending || !body.trim() || (composeMode === 'forward' ? !forwardTo.trim() : !recipientEmail)}
                  onClick={handleSend}
                >
                  {sending ? 'Sending...' : composeMode === 'forward' ? 'Forward email' : composeMode === 'reply' ? 'Send reply' : 'Send email'}
                </button>
              </div>
              {composeMode !== 'forward' && !recipientEmail && (
                <p style={{ fontSize: '0.72rem', color: '#ff9a9d', marginTop: '0.3rem' }}>
                  This customer has no email address on file.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerEmailsModal;
